import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, MapPin, CheckCircle, Heart, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { isValidCaliforniaZipCode } from '@/utils/zipCodeValidation';
import { validateZipCode } from '@/utils/zipCodeService';
import api from '@/lib/api';

const KIDS_AGES = [
  { value: "0-1", label: "0-1 years" },
  { value: "1-3", label: "1-3 years" },
  { value: "3-6", label: "3-6 years" },
  { value: "6-12", label: "6-12 years" },
  { value: "12+", label: "12+ years" }
];

// Flow steps
const STEP_ZIP_CHECK = 'zip_check';
const STEP_REGISTRATION = 'registration';
const STEP_WAITLIST = 'waitlist';
const STEP_WAITLIST_SUCCESS = 'waitlist_success';

export default function SignupPage() {
  const [step, setStep] = useState(STEP_ZIP_CHECK);
  const [zipCode, setZipCode] = useState('');
  const [zipError, setZipError] = useState('');
  const [checkingZip, setCheckingZip] = useState(false);
  const [searchParams] = useSearchParams();
  
  // Get tier, billing, and promo from URL params (from pricing page)
  const selectedTier = searchParams.get('tier');
  const selectedBilling = searchParams.get('billing') || 'monthly';
  const promoFromUrl = (searchParams.get('promo') || '').trim();
  // Shareable-event redirect (e.g. /signup?redirect=/events/:id)
  const redirectPath = searchParams.get('redirect') || '';
  useEffect(() => {
    if (redirectPath && redirectPath.startsWith('/')) {
      try { localStorage.setItem('post_onboarding_redirect', redirectPath); } catch { /* ignore */ }
    }
  }, [redirectPath]);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    city: '',
    zip_code: '',
    kids_ages: []
  });
  
  const [waitlistData, setWaitlistData] = useState({
    first_name: '',
    email: '',
    city: '',
    zip_code: ''
  });
  
  // Guest pass invitation (?guest_invite=TOKEN)
  const guestInviteToken = (searchParams.get('guest_invite') || '').trim();
  const [guestInvite, setGuestInvite] = useState(null);
  useEffect(() => {
    if (!guestInviteToken) return;
    api.get(`/guest-pass/invite/${guestInviteToken}`)
      .then((res) => {
        setGuestInvite(res.data);
        setFormData((f) => ({
          ...f,
          first_name: f.first_name || res.data.guest_first_name || '',
          email: f.email || res.data.guest_email || '',
        }));
        setStep(STEP_ZIP_CHECK);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'This invitation is no longer valid');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInviteToken]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Persist member referral code from URL (?ref=CMC-XXXXXX) for the duration of the visit.
  useEffect(() => {
    const ref = (searchParams.get('ref') || '').trim();
    if (ref && /^CMC-/i.test(ref)) {
      try { localStorage.setItem('cmc_member_ref', ref.toUpperCase()); } catch (_) { /* ignore */ }
    }
  }, [searchParams]);

  // Handle zip code input (only numbers, max 5)
  const handleZipInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(value);
    setZipError('');
  };

  // Check zip code and route user using backend API
  const handleZipCheck = async () => {
    if (!zipCode || zipCode.length !== 5) {
      setZipError('Please enter a 5-digit zip code');
      return;
    }
    
    setCheckingZip(true);
    
    try {
      const result = await validateZipCode(zipCode);
      
      if (!result.valid) {
        setZipError(result.error || 'Invalid zip code');
        setCheckingZip(false);
        return;
      }
      
      if (result.route === 'signup') {
        // LA County - go to full registration with actual city name
        setFormData(prev => ({
          ...prev,
          zip_code: zipCode,
          city: result.city  // Use the real city name from pgeocode
        }));
        setStep(STEP_REGISTRATION);
        toast.success(result.message);
      } else {
        // Not in LA - go to waitlist with actual city name
        setWaitlistData(prev => ({
          ...prev,
          zip_code: zipCode,
          city: result.city  // Use the real city name from pgeocode
        }));
        setStep(STEP_WAITLIST);
        toast.info(result.message);
      }
    } catch (error) {
      setZipError('Failed to validate zip code. Please try again.');
    } finally {
      setCheckingZip(false);
    }
  };

  // Handle registration form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'zip_code') {
      if (value && value.length === 5) {
        if (!isValidCaliforniaZipCode(value)) {
          setZipCodeError('Please enter a valid California zip code');
        } else {
          setZipCodeError('');
          // Auto-populate city from zip code
          lookupAndSetCity(value);
        }
      } else {
        setZipCodeError('');
      }
    }
  };

  // Look up city from zip code and update form
  const lookupAndSetCity = async (zip) => {
    try {
      const result = await validateZipCode(zip);
      if (result.valid && result.city) {
        setFormData(prev => ({ ...prev, city: result.city }));
      }
    } catch (error) {
      console.error('Failed to lookup city:', error);
    }
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    handleChange('zip_code', value);
  };

  const toggleKidsAge = (age) => {
    setFormData(prev => ({
      ...prev,
      kids_ages: prev.kids_ages.includes(age)
        ? prev.kids_ages.filter(a => a !== age)
        : [...prev.kids_ages, age]
    }));
  };

  // Submit registration
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.city) {
      toast.error('Please select your city');
      return;
    }

    if (!formData.zip_code) {
      toast.error('Please enter your zip code');
      return;
    }

    if (!isValidCaliforniaZipCode(formData.zip_code)) {
      toast.error('Please enter a valid California zip code');
      return;
    }
    
    if (formData.kids_ages.length === 0) {
      toast.error('Please select at least one age range');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      // Attach ref_code from QR tracking if present
      const ref = localStorage.getItem('cmc_ref');
      if (ref) formData.ref_code = ref;
      // Attach member referral code from ?ref=CMC-XXXXXX (or stored in localStorage)
      const memberRef = (searchParams.get('ref') || localStorage.getItem('cmc_member_ref') || '').trim();
      if (memberRef && /^CMC-/i.test(memberRef)) {
        formData.referral_code = memberRef.toUpperCase();
      }
      // Attach guest pass invite token so the free workshop seat is claimed on signup
      if (guestInviteToken && guestInvite) {
        formData.guest_invite_token = guestInviteToken;
      }
      await signup(formData);
      // Clear referral cookie after successful signup
      try { localStorage.removeItem('cmc_member_ref'); } catch (_) { /* ignore */ }
      toast.success('Welcome to Cali Moms Connect!');

      // If a promo code was provided in the URL (from pricing page), apply it now
      // so the user is granted free access BEFORE we even consider Stripe checkout.
      let promoApplied = false;
      if (promoFromUrl) {
        try {
          await api.post('/promo/apply', { code: promoFromUrl });
          toast.success(`Promo ${promoFromUrl} applied — no payment required!`);
          promoApplied = true;
        } catch (err) {
          toast.error(err.response?.data?.detail || `Promo code ${promoFromUrl} could not be applied.`);
        }
      }

      // If user came from pricing page with a tier selected
      if (selectedTier === 'connect' || selectedTier === 'gather' || selectedTier === 'free' || promoApplied) {
        // Set free Connect tier (if not already promo-upgraded) and go to dashboard
        if (!promoApplied) {
          try {
            await api.post('/stripe/free-tier');
            toast.success("Welcome to Connect!");
          } catch (err) { /* continue */ }
        }
        navigate('/dashboard');
      } else if (selectedTier === 'thrive') {
        // Redirect to Stripe checkout for Thrive
        try {
          const response = await api.post('/stripe/checkout', {
            tier: 'thrive',
            origin_url: window.location.origin
          });
          if (response.data.url) {
            window.location.href = response.data.url;
          } else {
            navigate('/onboarding');
          }
        } catch (err) {
          toast.error('Failed to start checkout. You can upgrade later from the pricing page.');
          navigate('/onboarding');
        }
      } else {
        // No tier selected, go to normal onboarding
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Handle waitlist form changes
  const handleWaitlistChange = (field, value) => {
    setWaitlistData(prev => ({ ...prev, [field]: value }));
  };

  // Submit waitlist
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    if (!waitlistData.first_name || !waitlistData.email || !waitlistData.city) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/waitlist/signup', waitlistData);
      setStep(STEP_WAITLIST_SUCCESS);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-seafoam/20 via-warm-white to-cream font-body">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-seafoam/10 rounded-full blur-3xl" />
      </div>

      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="py-6 px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-block">
              <span className="font-display text-2xl text-navy">
                Cali<span className="italic-teal">Moms</span> Connect
              </span>
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <AnimatePresence mode="wait">
            {/* Step 1: Zip Code Check */}
            {step === STEP_ZIP_CHECK && (
              <motion.div
                key="zip-check"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full max-w-md"
              >
                <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-seafoam/30 rounded-full flex items-center justify-center mx-auto mb-6">
                      <MapPin className="text-teal" size={28} />
                    </div>
                    <h1 className="font-display text-3xl text-navy mb-3">
                      Let's check your area
                    </h1>
                    <p className="text-text-mid">
                      First, let us see if we're live in your neighborhood yet!
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="zip-check" className="text-navy font-medium">
                        What is your zip code?
                      </Label>
                      <Input
                        id="zip-check"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={5}
                        value={zipCode}
                        onChange={handleZipInput}
                        onKeyDown={(e) => e.key === 'Enter' && handleZipCheck()}
                        placeholder="Enter your 5-digit zip code"
                        className={`h-14 text-lg text-center rounded-xl border-seafoam focus:border-teal ${
                          zipError ? 'border-red-400 focus:border-red-500' : ''
                        }`}
                        data-testid="signup-zip-check-input"
                      />
                      {zipError && (
                        <p className="text-red-500 text-sm text-center">{zipError}</p>
                      )}
                    </div>

                    <Button
                      onClick={handleZipCheck}
                      disabled={checkingZip || zipCode.length !== 5}
                      className="w-full h-14 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base"
                      data-testid="signup-zip-check-btn"
                    >
                      {checkingZip ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={20} />
                          Checking...
                        </>
                      ) : (
                        <>
                          Check My Area <ArrowRight size={20} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-center text-text-mid mt-8 text-sm">
                    Already have an account?{' '}
                    <Link to="/login" className="text-teal hover:text-teal-deep font-semibold">
                      Sign In
                    </Link>
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 2A: Full Registration (LA County) */}
            {step === STEP_REGISTRATION && (
              <motion.div
                key="registration"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full max-w-lg"
              >
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                  {/* Guest invitation banner */}
                  {guestInvite && (
                    <div className="bg-amber-50 border-b border-amber-200 p-4 text-center" data-testid="guest-invite-banner">
                      <p className="text-sm text-navy">
                        <strong>{guestInvite.invited_by}</strong> invited you to{' '}
                        <strong>{guestInvite.event?.title}</strong> — your free seat is reserved.
                        Create your free account to claim it.
                      </p>
                    </div>
                  )}
                  {/* Success Banner */}
                  <div className="bg-gradient-to-r from-teal to-teal-deep p-6 text-white text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle size={24} />
                      <span className="font-semibold">Great news!</span>
                    </div>
                    <p className="text-white/90">
                      Cali Moms Connect is live in your area!
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleRegistrationSubmit} className="p-8 space-y-5" data-testid="signup-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-navy font-medium">First Name</Label>
                        <Input
                          id="first_name"
                          value={formData.first_name}
                          onChange={(e) => handleChange('first_name', e.target.value)}
                          placeholder="Jane"
                          required
                          data-testid="signup-first-name-input"
                          className="h-12 rounded-xl border-seafoam focus:border-teal"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-navy font-medium">Last Name</Label>
                        <Input
                          id="last_name"
                          value={formData.last_name}
                          onChange={(e) => handleChange('last_name', e.target.value)}
                          placeholder="Smith"
                          required
                          data-testid="signup-last-name-input"
                          className="h-12 rounded-xl border-seafoam focus:border-teal"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-navy font-medium">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="jane@example.com"
                        required
                        data-testid="signup-email-input"
                        className="h-12 rounded-xl border-seafoam focus:border-teal"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-navy font-medium">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          placeholder="Create a password"
                          required
                          minLength={6}
                          data-testid="signup-password-input"
                          className="h-12 rounded-xl border-seafoam focus:border-teal pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-text-light hover:text-teal"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="zip_code" className="text-navy font-medium">Zip Code</Label>
                        <Input
                          id="zip_code"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={5}
                          value={formData.zip_code}
                          onChange={handleZipCodeChange}
                          placeholder="90210"
                          required
                          data-testid="signup-zip-code-input"
                          className={`h-12 rounded-xl border-seafoam focus:border-teal ${
                            zipCodeError ? 'border-red-400 focus:border-red-500' : ''
                          }`}
                        />
                        {zipCodeError && (
                          <p className="text-red-500 text-xs">{zipCodeError}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-navy font-medium">City</Label>
                        <Input
                          value={formData.city}
                          readOnly
                          placeholder="Auto-populated from zip code"
                          data-testid="signup-city-input"
                          className="h-12 rounded-xl border-seafoam bg-gray-50 text-navy"
                        />
                        {formData.city && (
                          <p className="text-xs text-teal flex items-center gap-1">
                            <MapPin size={12} />
                            City detected from zip code
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-navy font-medium">Kids Ages (select all that apply)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {KIDS_AGES.map(age => (
                          <label 
                            key={age.value}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                              formData.kids_ages.includes(age.value)
                                ? 'border-teal bg-teal/5'
                                : 'border-seafoam hover:border-teal/50'
                            }`}
                          >
                            <Checkbox
                              checked={formData.kids_ages.includes(age.value)}
                              onCheckedChange={() => toggleKidsAge(age.value)}
                              data-testid={`signup-age-${age.value}`}
                            />
                            <span className="text-sm text-navy">{age.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Terms Agreement */}
                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="terms-agreement"
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => setAgreedToTerms(checked)}
                        data-testid="terms-agreement-checkbox"
                        className="mt-0.5"
                      />
                      <label 
                        htmlFor="terms-agreement" 
                        className="text-sm text-text-mid leading-relaxed cursor-pointer"
                      >
                        I agree to the{' '}
                        <Link 
                          to="/terms" 
                          target="_blank"
                          className="text-teal hover:text-teal-deep underline"
                          data-testid="signup-terms-link"
                        >
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link 
                          to="/privacy" 
                          target="_blank"
                          className="text-teal hover:text-teal-deep underline"
                          data-testid="signup-privacy-link"
                        >
                          Privacy Policy
                        </Link>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !agreedToTerms}
                      data-testid="signup-submit-btn"
                      className="w-full h-12 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={18} />
                          Creating Account...
                        </>
                      ) : (
                        <>Create Account <ArrowRight size={18} className="ml-2" /></>
                      )}
                    </Button>
                  </form>

                  <p className="text-center text-text-mid pb-8 text-sm">
                    Already have an account?{' '}
                    <Link to="/login" className="text-teal hover:text-teal-deep font-semibold">
                      Sign In
                    </Link>
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 2B: Waitlist Form (Non-LA County) */}
            {step === STEP_WAITLIST && (
              <motion.div
                key="waitlist"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full max-w-md"
              >
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-br from-seafoam/30 to-cream p-8 text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <Heart className="text-teal" size={28} />
                    </div>
                    <h2 className="font-display text-2xl text-navy mb-3">
                      We're not in your area yet
                    </h2>
                    <p className="text-text-mid">
                      But we're growing fast! Join our waitlist and be the first to know when we launch in your city.
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleWaitlistSubmit} className="p-8 space-y-5" data-testid="waitlist-form">
                    <div className="space-y-2">
                      <Label htmlFor="waitlist-name" className="text-navy font-medium">
                        First Name
                      </Label>
                      <Input
                        id="waitlist-name"
                        value={waitlistData.first_name}
                        onChange={(e) => handleWaitlistChange('first_name', e.target.value)}
                        placeholder="Your first name"
                        className="h-12 rounded-xl border-seafoam focus:border-teal"
                        data-testid="waitlist-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="waitlist-email" className="text-navy font-medium">
                        Email Address
                      </Label>
                      <Input
                        id="waitlist-email"
                        type="email"
                        value={waitlistData.email}
                        onChange={(e) => handleWaitlistChange('email', e.target.value)}
                        placeholder="you@example.com"
                        className="h-12 rounded-xl border-seafoam focus:border-teal"
                        data-testid="waitlist-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="waitlist-city" className="text-navy font-medium">
                        Your City
                      </Label>
                      <Input
                        id="waitlist-city"
                        value={waitlistData.city}
                        readOnly
                        placeholder="Auto-populated from zip code"
                        className="h-12 rounded-xl border-seafoam bg-gray-50 text-navy"
                        data-testid="waitlist-city-input"
                      />
                      {waitlistData.city && (
                        <p className="text-xs text-teal flex items-center gap-1">
                          <MapPin size={12} />
                          {waitlistData.city}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="waitlist-zip" className="text-navy font-medium">
                        Zip Code
                      </Label>
                      <Input
                        id="waitlist-zip"
                        type="text"
                        inputMode="numeric"
                        value={waitlistData.zip_code}
                        readOnly
                        className="h-12 rounded-xl border-seafoam bg-gray-50 text-navy"
                        data-testid="waitlist-zip-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-teal hover:bg-teal-deep text-white rounded-full font-bold text-base mt-2"
                      data-testid="waitlist-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={18} />
                          Joining...
                        </>
                      ) : (
                        'Join the Waitlist'
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setStep(STEP_ZIP_CHECK)}
                      className="w-full text-sm text-text-mid hover:text-teal transition-colors"
                    >
                      ← Try a different zip code
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* Step 3: Waitlist Success */}
            {step === STEP_WAITLIST_SUCCESS && (
              <motion.div
                key="waitlist-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md"
              >
                <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
                  <div className="w-20 h-20 bg-seafoam/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-teal" size={40} />
                  </div>
                  <h2 className="font-display text-3xl text-navy mb-4">
                    You're on the list!
                  </h2>
                  <p className="text-text-mid leading-relaxed mb-6">
                    We'll notify you as soon as Cali Moms Connect launches in your area. Keep an eye on your inbox!
                  </p>
                  <div className="bg-cream rounded-xl p-5 mb-8">
                    <p className="text-text-mid italic">
                      "Motherhood is better together."
                    </p>
                  </div>
                  <Link to="/">
                    <Button
                      className="bg-teal hover:bg-teal-deep text-white rounded-full px-10 h-12"
                      data-testid="waitlist-done-btn"
                    >
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
